/**
 * Custom field helpers.
 *
 * The API returns custom field values on `item.custom_fields`: an object keyed by the
 * field name, lowercased with spaces turned into underscores (see the jsonb_object_agg
 * in server/routes/items.js). Column and filter keys prefix that with `custom__`, so a
 * custom field is never a plain property on the item and cannot be read with item[key].
 */

export const CUSTOM_FIELD_PREFIX = 'custom__';

export const normalizeCustomFieldKey = (value) => {
  if (value == null) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
};

export const isCustomFieldKey = (key) =>
  typeof key === 'string' && key.startsWith(CUSTOM_FIELD_PREFIX);

export const customFieldColumnKey = (field) =>
  `${CUSTOM_FIELD_PREFIX}${normalizeCustomFieldKey(field?.name || field?.label)}`;

/** Read a custom field off an item. Accepts either `custom__foo` or a bare `foo`. */
export const readCustomFieldValue = (item, key) => {
  const customFields = item?.custom_fields;
  if (!customFields || typeof customFields !== 'object' || Array.isArray(customFields)) {
    return undefined;
  }

  const target = normalizeCustomFieldKey(
    isCustomFieldKey(key) ? key.slice(CUSTOM_FIELD_PREFIX.length) : key
  );
  if (!target) return undefined;

  for (const rawKey in customFields) {
    if (
      Object.prototype.hasOwnProperty.call(customFields, rawKey) &&
      normalizeCustomFieldKey(rawKey) === target
    ) {
      return customFields[rawKey];
    }
  }

  return undefined;
};

/**
 * Columns whose key is not the property a person filters on: the table sorts by
 * one property but displays another (stage order vs. stage name, project id vs.
 * project name). Filters follow what the column shows, so both the dropdown
 * options and the evaluation resolve through here.
 */
export const FILTER_FIELD_ALIASES = {
  current_stage_order: 'current_stage_name',
  project_id: 'project_name'
};

export const resolveFilterField = (field) => FILTER_FIELD_ALIASES[field] || field;

/**
 * Evaluate one custom filter row against an item.
 *
 * Reads standard fields off the item directly and custom fields out of
 * `item.custom_fields`. Values may be a scalar or an array (multi-select).
 */
export const evaluateItemFilter = (item, filter) => {
  const { operator, value } = filter || {};
  const field = resolveFilterField(filter?.field);
  if (!field || !operator) return true;

  const fieldValue = isCustomFieldKey(field)
    ? readCustomFieldValue(item, field)
    : item?.[field];

  const stringValue = fieldValue?.toString() || '';
  const valuesAsArray = Array.isArray(value) ? value : [value];
  const includesText = (needle) =>
    stringValue.toLowerCase().includes(String(needle ?? '').toLowerCase());

  const isDateField = field.includes('_on') || field.includes('_at');

  // Ids are displayed zero-padded (item 1 reads as "001"), so a filter typed
  // the way it looks on screen has to match the plain integer behind it.
  // Scoped to id fields: elsewhere "0123" and "123" are different strings.
  const isIdField = field === 'id' || /_id$/.test(field);
  const equalsValue = (v) => {
    const text = String(v ?? '').trim();
    if (stringValue === text) return true;
    if (!isIdField || text === '' || stringValue === '') return false;

    const left = Number(stringValue);
    const right = Number(text);
    return Number.isInteger(left) && Number.isInteger(right) && left === right;
  };

  switch (operator) {
    case 'equals':
      return valuesAsArray.some(equalsValue);

    case 'not_equals':
      return !valuesAsArray.some(equalsValue);

    case 'contains':
      return valuesAsArray.some(includesText);

    case 'not_contains':
      return !valuesAsArray.some(includesText);

    case 'has_value':
      return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

    case 'does_not_have_value':
      return fieldValue === null || fieldValue === undefined || fieldValue === '';

    case 'greater_than':
      if (isDateField) return new Date(fieldValue) > new Date(valuesAsArray[0]);
      return parseFloat(fieldValue) > parseFloat(valuesAsArray[0]);

    case 'less_than':
      if (isDateField) return new Date(fieldValue) < new Date(valuesAsArray[0]);
      return parseFloat(fieldValue) < parseFloat(valuesAsArray[0]);

    default:
      return true;
  }
};

/** Operators that are meaningless without a value on the right-hand side. */
const VALUE_REQUIRED_OPERATORS = [
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'greater_than',
  'less_than'
];

export const FILTER_CONJUNCTIONS = ['AND', 'OR'];

export const normalizeFilterConjunction = (value) =>
  String(value || '').trim().toUpperCase() === 'OR' ? 'OR' : 'AND';

/**
 * A row only takes part in the expression once it can actually be evaluated.
 * Half-built rows are dropped rather than treated as true, because under OR a
 * "true" placeholder would match every record.
 */
export const isFilterComplete = (filter) => {
  if (!filter || !filter.field || !filter.operator) return false;
  if (!VALUE_REQUIRED_OPERATORS.includes(filter.operator)) return true;

  const { value } = filter;
  if (Array.isArray(value)) return value.length > 0;
  return value !== '' && value !== null && value !== undefined;
};

/**
 * Split filter rows into AND groups, following the operator each row carries in
 * `conjunction` (the join to the row above it; the first row has none).
 *
 * AND binds tighter than OR, so `a AND b OR c` groups as `(a AND b) OR c`.
 * Returns the row indexes, grouped: [[0, 1], [2]].
 */
export const groupFilterIndexes = (filters) => {
  const groups = [];

  (Array.isArray(filters) ? filters : []).forEach((filter, index) => {
    if (index === 0 || normalizeFilterConjunction(filter?.conjunction) === 'OR') {
      groups.push([]);
    }
    groups[groups.length - 1].push(index);
  });

  return groups;
};

/**
 * Evaluate a whole filter list against an item as an OR of AND groups.
 * An empty (or entirely incomplete) list matches everything.
 */
export const evaluateItemFilters = (item, filters) => {
  const active = (Array.isArray(filters) ? filters : []).filter(isFilterComplete);
  if (active.length === 0) return true;

  return groupFilterIndexes(active).some((group) =>
    group.every((index) => evaluateItemFilter(item, active[index]))
  );
};

/**
 * Dropdown options for filtering on a custom field, or null when the field is free-text.
 * yesno fields store the literal strings 'Yes'/'No'; select fields store option_value.
 */
export const customFieldFilterOptions = (field) => {
  const type = String(field?.type || '').toLowerCase();

  if (type === 'yesno') {
    return [
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' }
    ];
  }

  if (type === 'select' || type === 'multiselect') {
    const mapped = (Array.isArray(field?.options) ? field.options : [])
      .map((option) => {
        const value = option?.option_value ?? option?.value;
        if (value == null || value === '') return null;
        return {
          value: String(value),
          label: String(option?.option_label ?? option?.label ?? value)
        };
      })
      .filter(Boolean);

    return mapped.length > 0 ? mapped : null;
  }

  return null;
};
